import {getApolloClient, ApolloClientInstance} from "@atg-tillsammans/apollo-data-access";
import {MutableMemberTeamFieldsFragmentDoc} from "@atg-tillsammans/graphql/fragment/__generated__/teamFragments.graphql.generated";
import {
    CreateApplicationDocument,
    type CreateApplicationMutation,
} from "@atg-tillsammans/graphql/mutation/__generated__/applicationMutations.graphql.generated";
import {
    CreateTeamDocument,
    type CreateTeamMutationVariables,
    type CreateTeamMutation,
} from "@atg-tillsammans/graphql/mutation/__generated__/teamMutations.graphql.generated";
import {TeamMembersDocument} from "@atg-tillsammans/graphql/query/__generated__/memberQueries.graphql.generated";

import type {TeamMembersQuery} from "@atg-tillsammans/graphql/query/__generated__/memberQueries.graphql.generated";
import type {TeamID, MemberTeam} from "@atg-tillsammans/types";
import type {CreateApplicationInput} from "@atg-tillsammans/types/generated";

export const MemberTeamResolvers = {
    incrementActivatedRoundsAmount: (team?: MemberTeam | null) => {
        if (!team) return undefined;
        return {
            ...team,
            activatedRoundsAmount: team.activatedRoundsAmount
                ? team.activatedRoundsAmount + 1
                : 1,
        };
    },
    incrementUnreadMessages: (team?: MemberTeam | null) => {
        if (!team) return undefined;
        return {
            ...team,
            unreadComments: team.unreadComments ? team.unreadComments + 1 : 1,
        };
    },
    updateUnreadMessages: (nrOfUnreadComments: number) => (team?: MemberTeam | null) => {
        if (!team) return undefined;
        return {
            ...team,
            unreadComments: nrOfUnreadComments,
        };
    },
};

export class TeamService {
    static incrementActivatedRoundsAmount(teamId: TeamID) {
        const client = getApolloClient();
        if (!client) return;
        client.cache.updateFragment(
            {
                id: `MemberTeam:${teamId}`,
                fragment: MutableMemberTeamFieldsFragmentDoc,
            },
            MemberTeamResolvers.incrementActivatedRoundsAmount,
        );
    }

    static incrementUnreadMessages(teamId: TeamID) {
        const client = getApolloClient();
        if (!client) return;
        client.cache.updateFragment(
            {
                id: `MemberTeam:${teamId}`,
                fragment: MutableMemberTeamFieldsFragmentDoc,
            },
            MemberTeamResolvers.incrementUnreadMessages,
        );
    }

    static updateUnreadMessages(teamId: TeamID, nrOfUnreadComments: number) {
        const client = getApolloClient();
        if (!client) return;
        client.cache.updateFragment(
            {
                id: `MemberTeam:${teamId}`,
                fragment: MutableMemberTeamFieldsFragmentDoc,
            },
            (team?: MemberTeam | null) => {
                if (!team) return undefined;
                return {
                    ...team,
                    unreadComments: nrOfUnreadComments,
                };
            },
        );
    }

    static async fetchTeamMembers(
        teamId: TeamID,
        {forcedFetch = false}: {forcedFetch?: boolean} = {},
    ) {
        // Note: If called outside the apollo context return null
        if (!ApolloClientInstance.exists()) return null;

        const client = getApolloClient();

        if (client) {
            const {data, loading, error} = await client.query<TeamMembersQuery>({
                query: TeamMembersDocument,
                variables: {
                    teamId,
                },
                fetchPolicy: forcedFetch ? "network-only" : "cache-first",
            });

            return {
                members: data?.teamMembers ?? [],
                isFetching: loading,
                hasError: !!error,
            };
        }
        return null;
    }

    static async createTeam(variables: CreateTeamMutationVariables) {
        const client = getApolloClient();
        if (client) {
            const {data, errors} = await client.mutate<CreateTeamMutation>({
                mutation: CreateTeamDocument,
                variables,
            });
            return {team: data?.createTeam, errors};
        }
        return null;
    }

    static async joinTeam(input: CreateApplicationInput) {
        const client = getApolloClient();
        if (client) {
            const {data, errors} = await client.mutate<CreateApplicationMutation>({
                mutation: CreateApplicationDocument,
                variables: {input},
            });
            return [data?.createApplication.status, errors];
        }
        return [undefined, undefined];
    }
}
